import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { faEyeSlash } from "@fortawesome/pro-regular-svg-icons/faEyeSlash";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, styled, Tooltip, Typography } from '@mui/material';
import { AppImages } from "app/appImages";
import { FileThumbnaildGetDto, RequestLifeCycle } from "app/appModels";
import { AppTransComponents } from "app/appTrans";
import { MprcButton, MprcCard, MprcClickable, MprcLink, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcText } from "components";
import { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { TopicAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { MprcSwitch } from "components/MprcSwitch";
import { Delta, EmitterSource, QuillCommands, QuillUnprivilegedEditor } from "components/Quill";
import {
    AsyncUploadResult,
    CreateOrUpdateQuestionCommand,
    GetPublicationAttachDto,
    GetQuestionPublicationDto,
    PublicationApprovalStatus,
    PublicationAttachFileItem,
    PublicationAttachType,
    PublicationCreationSettingsResponse,
    PublicationType,
    TopicAutoCompleteHierarchicalModelDto
} from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import LibUtils from "lib/LibUtils";
import LinkifyIt from "linkify-it";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { NIL, v4 as uuidv4 } from "uuid";
import { IStickerBoxValidation } from "views/foundation/sticker/components/StickerBox";
import { publish } from "views/knowledge/feed/FeedEvents";
import { ICreatePublicationProps } from "views/knowledge/publication/creation";
import {
    IInstitutionalBoxValidation,
    IPublicationChannelBoxValidation,
    isPresentationAttach,
    PublicationAdvancedBox,
    PublicationAttachFileHandler,
    PublicationAttachView,
    PublicationChannelBox,
    PublicationEditor,
    PublicationMediaView, PublicationMediaViewData,
    PublicationTopicsBox
} from "views/knowledge/publication/creation/components";
import { PublicationDialogContent } from "views/knowledge/publication/creation/components/PublicationDialogContent";
import { parseContentTopics } from "views/knowledge/publication/creation/CreatePost";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    topics?: boolean;
}

interface IState {
    record: GetQuestionPublicationDto;
    validation: IValidation & IPublicationChannelBoxValidation & IInstitutionalBoxValidation & IStickerBoxValidation;
    uploadingStatus: UploadingStatus;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const ContainerData = styled(Box)(
    ({ theme }) => ({
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: theme.palette.grey[200],
        marginBottom: "4px",
        padding: "4px",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        overflowY: "auto",
        "& .quill-container-editor .ql-container": {
            borderRadius: 0,
            border: "none",
        },
    })
);

const ContainerTextArea = styled(Box)(
    ({ theme }) => ({
        marginBottom: "10px",
        border: "none",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        "& > textarea": {
            border: "none",
            width: "100%",
            backgroundColor: "transparent",
            resize: "none",
            "&:focus": {
                outline: "none"
            }
        }
    })
);

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

type Props = Omit<ICreatePublicationProps, "parentId">;

export const CreateQuestion = (props: Props) => {
    const { onClose, mentions, responderId } = props;
    const qlEditorId = `ql${new Date().getTime()}`;
    const minHeight = 200;
    const session = useSession();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();

    const initialContentRef = useRef<string>("");
    const editorRef = useRef<QuillCommands | null>(null);
    const config = useRef<PublicationCreationSettingsResponse | null>(null);

    const [step, setStep] = useState<number>(1);
    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(mentions != undefined && mentions.length > 0);
    const [contentTopics, setContentTopics] = useState<AutoCompleteHierarchicalModel[]>([]);
    const [loadDto, setLoadDto] = useState<GetQuestionPublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const metadataProcessing = useRef<string[]>([]);

    const timerQuestionMark = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const timerLastChange = useRef<number | undefined>(0);
    const updateCaret = useRef<boolean>(false);

    const refTitle = useRef<HTMLTextAreaElement | null>(null);

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                content: "",
                title: "?",
                topics: [],
                attachs: [],
                channel: null!,
                isPrivate: false,
                isAnonymous: false,
                publicationType: PublicationType.Question,
                presentation: null,
                advancedOptions: {
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewStickerUntil: null,
                    sticker: null
                },
            },
            validation: {
                title: undefined,
                channel: undefined,
                topics: undefined,
                period: true,
                viewStickerUntil: true
            },

            uploadingStatus: "none",
        });

    ///
    const updateRecord = (data: Partial<GetQuestionPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const handleEditorChange = (content: string, delta: Delta, source: EmitterSource, editor: QuillUnprivilegedEditor) => {
        const html = editor.getHTML();

        setState({
            ...refState.current,
            record: updateRecord({ content: html })
        });

        analyse(content);
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: v }),
            validation: { ...state.validation, title: v.length > 0 }
        });
    };

    ///
    function updateTitleValue(value: string, updCaret: boolean = false) {
        updateCaret.current = updCaret;
        setState({
            ...state,
            record: updateRecord({ title: value }),
            validation: { ...state.validation, title: value.length > 0 }
        });
    }


    ///
    const handleTitleCaret = () => {
        // Reposiciona CARET antes do "?" do final do texto.
        if (refTitle.current) {
            if (refTitle.current.selectionStart == refTitle.current.selectionEnd) { // Garante que não está selecionando um texto
                if (refTitle.current.value.length > 1 && refTitle.current.selectionEnd > refTitle.current.value.length - 1) {
                    refTitle.current.selectionStart = refTitle.current.selectionEnd = refTitle.current.value.length - 1;
                }
            }
        }
    };

    ///
    const handleTitleKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {

        if (timerQuestionMark.current) clearTimeout(timerQuestionMark.current);

        timerLastChange.current = new Date().getTime();

        timerQuestionMark.current = setTimeout(() => {
            if ((new Date().getTime() - timerLastChange.current!) > 200 && refTitle.current) {
                //  Adiciona "?" no final do texto.
                if (refTitle.current.value.length > 0 && refTitle.current.value[refTitle.current.value.length - 1] != "?") {
                    updateTitleValue(refTitle.current.value + "?", true);
                }
            }
        }, 200);
    };

    ///
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        e.preventDefault();

        let pasteText = e.clipboardData.getData("text");

        if (pasteText.length > 0) {
            // Remove caracters especiais
            pasteText = pasteText.replace(/[^0-9a-zA-Z\u00C0-\u00FF\s,\.]/gi, '').trim();
            if (pasteText.length > 0) {

                // Atualiza Título
                if (timerQuestionMark.current) clearTimeout(timerQuestionMark.current);

                const cv = e.currentTarget.value;
                var newText = cv.endsWith("?") ? cv.substr(0, refTitle.current!.value.length - 1) : cv;
                if (newText.length > 0) newText += " ";
                newText += pasteText + "?";

                updateTitleValue(newText);
            }
        }
    };

    ///
    const handleIsAnonymous = (checked: boolean) => {
        setState({
            ...state,
            record: updateRecord({ isAnonymous: checked })
        });
    };

    ///
    const handlePresentation = (data: PublicationMediaViewData) => {
        setState({
            ...state,
            record: updateRecord({ presentation: data.presentation, attachs: data.attachs })
        });
    };

    ///
    const handleFileUploaded = (e: AsyncUploadResult) => {
        const data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        const attachType: PublicationAttachType = (PublicationAttachType[e.mainFile.metadata.type as any]) as any;
        const attachs = refState.current.record.attachs.splice(0);

        var at = {
            attachId: uuidv4(),
            file: {
                fileId: data.mainFile.fileId,
                metadata: data.mainFile.metadata,
                mimetype: data.mainFile.mimetype,
                url: data.mainFile.url
            },
            thumbnail: data.thumbnail == null
                ? null
                : {
                    fileId: data.thumbnail.fileId,
                    metadata: data.thumbnail.metadata,
                    mimetype: data.thumbnail.mimetype,
                    url: data.thumbnail.url
                },
            attachType: attachType,
            isEmbed: false,
            linkMetadata: null,
            videoProcessed: false,
            caption: null,
        } as GetPublicationAttachDto;

        attachs.push(at);

        const presentation = addToPresentation(at);

        setState({
            ...refState.current,
            record: updateRecord({ attachs: attachs, presentation: presentation })
        });
    };

    ///
    const handleUploadingStatus = (status: UploadingStatus) => {
        setState({ ...refState.current, uploadingStatus: status });
    };

    ///
    const analyse = (text: string) => {
        const attachs = refState.current.record.attachs.slice(0) ?? [];
        const linkify = new LinkifyIt();
        const matches = linkify.match(text);

        if (matches == null || matches.length == 0)
            return;

        matches.forEach((m) => {
            const existsRequest = metadataProcessing.current.some(x => x == m.url);
            if (!existsRequest && attachs.find(p => p.linkMetadata != null && p.linkMetadata.url == m.url) == null) {
                metadataProcessing.current.push(m.url);
                api.v1.urlParser.getMetadata({ url: m.url })
                    .then((meta) => {
                        const metadata = meta.data;
                        if (metadata != null) {

                            if (metadata.externalId == null || attachs.find(p => p.linkMetadata != null && p.linkMetadata.externalId == metadata.externalId) == null) {
                                const attachType: PublicationAttachType = (PublicationAttachType[metadata.provider as any]) as any;

                                var at = {
                                    attachId: uuidv4(),
                                    file: null,
                                    thumbnail: null,
                                    attachType: attachType,
                                    isEmbed: false,
                                    linkMetadata: metadata,
                                    videoProcessed: false,
                                    caption: null,
                                } as GetPublicationAttachDto;
                                attachs.push(at);

                                const presentation = addToPresentation(at);

                                setState({
                                    ...refState.current,
                                    record: updateRecord({ attachs: attachs, presentation: presentation })
                                });
                            }
                        }
                    })
                    .catch(() => {
                        enqueueSnackbar(t("Publication.Common.NotIdentifyTypeMessage"), { variant: "error" });
                    })
                    .finally(() => {
                        metadataProcessing.current = metadataProcessing.current.filter(x => x != m.url);
                    });
            }
        });
    };

    ///
    const addToPresentation = (attach: GetPublicationAttachDto) => {
        let presentation = refState.current.record.presentation;

        if (isPresentationAttach(attach.attachType)) {
            if (!presentation) {
                presentation = { orderedItems: [attach.attachId], rotationInterval: 0 };
            }
            else {
                presentation = { ...presentation, orderedItems: [...presentation.orderedItems, attach.attachId] };
            }
        }
        return presentation;
    };

    ///
    const setDefaultValues = () => {
        if (config.current && config.current.defaultChannel.length > 0) {
            const item = config.current.defaultChannel[0];
            setState({
                ...state,
                record: updateRecord({ channel: { channelId: item.key, name: item.label, isPrivate: item.isPrivate, thumbnailUrl: item.thumbnailUrl! } }),
                validation: { ...state.validation, channel: true }
            });
        }
        if (config.current && config.current.topics.length > 0) {
            setState({
                ...state,
                record: updateRecord({ topics: config.current.topics }),
            });
        }
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: props.channelId ?? null, defaultTopicId: props.topicId ?? null })
            .then((result) => {
                config.current = result.data;
                setDefaultValues();
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        Promise.all([
            api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null }),
            api.v1.knowledgePublication.getQuestionPublication({ publicationId })
        ])
            .then((results) => {
                config.current = results[0].data;

                const record = results[1].data;
                setState({
                    uploadingStatus: "none",
                    record,
                    validation: {
                        channel: true,
                        topics: true,
                        title: true,
                        period: true,
                        viewStickerUntil: true
                    }
                });
                setLoadDto(LibUtils.deepCopy(record));
                initialContentRef.current = record.content ?? "";

                setFormCycle(RequestLifeCycle.None);
                if ((record.content && record.content != "<p><br></p>") || (record.attachs && record.attachs.length > 0)) {
                    setExpanded(true);
                }
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = () => {
        const { record } = state;

        const files: PublicationAttachFileItem[] = record.attachs.map((v) => ({
            attachId: v.attachId,
            fileId: v.file?.fileId ?? null,
            thumbnailId: v.thumbnail?.fileId ?? null,
            cardImageId: v.cardImage?.fileId ?? null,
            linkMetadata: v.linkMetadata,
            caption: v.caption
        }));

        const dto: CreateOrUpdateQuestionCommand = {
            publicationId: record.publicationId,
            title: record.title!,
            content: record.content,
            attachs: files,
            presentation: record.presentation,
            topics: record.topics.map((item) => item.key),
            channelId: record.channel.channelId,
            isPrivate: record.isPrivate,
            isAnonymous: record.isAnonymous,
            threadId: null,
            contentUnformatted: null,
            responderId: responderId ?? null,
            advancedOptions: {
                isInstitutional: record.advancedOptions.isInstitutional,
                stickerId: record.advancedOptions.sticker?.stickerId ?? null,
                viewStickerUntil: record.advancedOptions.viewStickerUntil,
                viewPeriodEnd: record.advancedOptions.viewPeriodEnd,
                viewPeriodStart: record.advancedOptions.viewPeriodStart
            }
        };

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.knowledgePublication.createOrUpdateQuestionPublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);

                if (response.data.newPublicationId) {

                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationCreated", response.data.newPublicationId);
                        publish("QuestionCreated", response.data.newPublicationId);
                        publish("AuthorNewPublication", session.user.userId);
                        publish("ChannelNewPublication", dto.channelId);
                        dto.topics.forEach(topicId => publish("TopicNewPublication", topicId));

                        enqueueSnackbar(
                            t("Publication.CreateQuestion.PublishMessage")
                            , {
                                variant: "mprcAction",
                                action: <Link to={KnowledgePaths.PublicationViewerResolver(response.data.newPublicationId, PublicationType.Question)}>{t("Publication.Messages.OpenPublication")}</Link>
                            }
                        );
                    }
                    else {
                        enqueueSnackbar(t("Publication.CreateQuestion.PublishModerationMessage"), { variant: "warning" });
                    }
                }
                else {
                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationUpdate", record.publicationId);
                        enqueueSnackbar(t("Publication.CreateQuestion.PublishUpdateMessage"), { variant: "success" });
                    }
                    else {
                        enqueueSnackbar(t("Publication.CreateQuestion.PublishModerationUpdateMessage"), { variant: "warning" });
                    }
                }
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const goStep2 = () => {
        initialContentRef.current = refState.current.record.content ?? "";
        const currentTopics = refState.current.record.topics;

        const topics = parseContentTopics(refState.current.record.content ?? "", currentTopics as TopicAutoCompleteHierarchicalModel[]);

        topics.filter(p => p.selectable).forEach((topic) => {
            if (currentTopics.find(p => p.key == topic.key) == null && currentTopics.length < 5) {
                currentTopics.push(topic as TopicAutoCompleteHierarchicalModelDto);
                topic.selectable = false;
            }
        });

        setContentTopics(topics);
        updateRecord({ topics: currentTopics });

        setStep(2);
    };

    ///
    const canGoStep2 = () => {
        const { validation, uploadingStatus } = state;
        return uploadingStatus == "uploading"
            ? false
            : (validation.title);
    };

    ///
    const canSave = (): boolean => {
        const { validation, uploadingStatus } = state;

        return uploadingStatus == "uploading"
            ? false
            : validation.channel === true && validation.period === true && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true) && recordChanged;
    };

    ///
    const getContinueButton = () => {
        return (
            <MprcButton
                variant="contained"
                color="primary"
                disabled={!canGoStep2()}
                onClick={() => goStep2()}
            >
                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
            </MprcButton>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Renders Auxiliares
    ///////////////////////////////////////////////////////////////////////

    const renderThumbnail = () => {

        const thumbTitle = t("Publication.CreateQuestion.ThumbTitle");

        return (
            <MprcClickable onClick={() => handleIsAnonymous(!state.record.isAnonymous)}>
                {
                    state.record.isAnonymous
                        ? <Avatar sx={{ width: 89, height: 89, margin: 1 }} title={thumbTitle}><FontAwesomeIcon icon={faEyeSlash} size="2x" /></Avatar>
                        : <Avatar sx={{ width: 89, height: 89, margin: 1 }} src={session.user.thumbnailUrl ?? AppImages.UserThumbnail} title={thumbTitle} />
                }
            </MprcClickable>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[StepFirstPage] = () => {
        const { record, validation } = state;

        return (
            <>
                <PublicationDialogContent>

                    <ContainerData>
                        <Grid container
                            alignContent="center"
                            sx={{
                                borderBottomWidth: 1,
                                borderBottomStyle: "dashed",
                                borderBottomColor: (theme) => theme.palette.grey[200],
                            }}
                        >
                            <Grid>
                                {renderThumbnail()}
                            </Grid>
                            <Grid style={{ flexGrow: 1 }}>
                                <ContainerTextArea>
                                    <textarea
                                        autoFocus
                                        name="title"
                                        placeholder={t("Publication.CreateQuestion.TitlePlaceholder")}
                                        autoComplete="off"
                                        maxLength={240}
                                        value={record.title}
                                        rows={3}
                                        tabIndex={0}
                                        ref={(ref) => { refTitle.current = ref; }}
                                        style={{ fontSize: "1.8em" }}
                                        onChange={(e) => handleTitleChange(e)}
                                        onKeyUp={handleTitleKeyUp}
                                        onPaste={handlePaste}
                                    />
                                </ContainerTextArea>

                                <Grid container alignContent="center" alignItems="center">
                                    <Grid>
                                        <MprcSwitch
                                            label={t("Publication.CreateQuestion.IsAnonymousLabel")}
                                            checked={state.record.isAnonymous}
                                            onChange={(e) => handleIsAnonymous(e.target.checked)}
                                        />
                                    </Grid>
                                    <Grid>
                                        <MprcText color="muted">
                                            <Tooltip title={t("Publication.CreateQuestion.AnonymousTooltip")}>
                                                <FontAwesomeIcon icon={faCircleInfo} />
                                            </Tooltip>
                                        </MprcText>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {
                            !expanded &&
                            <MprcLink
                                tabIndex={0}
                                margin={1}
                                onClick={() => setExpanded(true)}
                                sx={{
                                    color: (theme) => theme.palette.primary.light
                                }}
                            >
                                <FontAwesomeIcon icon={faCaretDown} />&nbsp;<span>{t("Publication.CreateQuestion.TitleExpandText")}</span>
                            </MprcLink>
                        }

                        {
                            expanded &&
                            <PublicationEditor
                                id={qlEditorId}
                                minHeight={minHeight}
                                toolbar={false}
                                initialValue={initialContentRef.current}
                                readOnly={false}
                                placeholder={t("Publication.CreateQuestion.EditorPlaceholder")}
                                onChange={handleEditorChange}
                                ref={editorRef}
                            />
                        }

                    </ContainerData>

                    {
                        expanded &&
                        <>
                            <PublicationMediaView
                                data={
                                    {
                                        presentation: record.presentation,
                                        attachs: record.attachs,
                                    }
                                }
                                onChange={handlePresentation}
                                onUploading={(uploading) => handleUploadingStatus(uploading ? "uploading" : "none")}
                            />

                            <PublicationAttachView
                                attachs={record.attachs}
                                onChange={
                                    (attachs) => setState({
                                        ...state,
                                        record: updateRecord({ attachs }),
                                        validation: { ...state.validation, ...validation }
                                    })
                                }
                            />

                            <PublicationAttachFileHandler
                                publicationId={record.publicationId}
                                attachs={record.attachs}
                                allowAttachVideo={config.current?.allowAttachVideo}
                                maxSize={config.current?.maxAttachSize ?? undefined}
                                onUploadComplete={handleFileUploaded}
                                onUploadStatusChange={handleUploadingStatus}
                            />
                        </>
                    }
                </PublicationDialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            <b>{t("Publication.CreateQuestion.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.CreatePost.Footer2")}</span>
                        </Grid>
                        <Grid>
                            {
                                getContinueButton()
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <DialogContent>

                    <PublicationChannelBox
                        channel={record.channel}
                        suggestions={config.current!.channelSuggestions}
                        onUpdateState={
                            ({ channel, validation }) => setState({
                                ...state,
                                record: updateRecord({ channel: channel! }),
                                validation: { ...state.validation, ...validation }
                            })
                        }
                        validation={validation}
                    />

                    <PublicationTopicsBox
                        channelId={record.channel?.channelId}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        publication={{ title: record.title, content: record.content }}
                        suggestions={contentTopics}
                        onUpdateState={
                            (topics) => setState({
                                ...state,
                                record: updateRecord({ topics: topics as TopicAutoCompleteHierarchicalModelDto[] })
                            })
                        }
                    />

                    <PublicationAdvancedBox
                        channelId={record.channel?.channelId}
                        allowInstitutionalPublisher={config.current?.allowInstitutionalPublisher ?? false}
                        onUpdateState={({ advancedOptions, validation }) => {
                            setState({
                                ...state,
                                record: updateRecord({ advancedOptions: { ...advancedOptions } }),
                                validation: { ...state.validation, ...validation }
                            });
                        }}
                        advancedOptions={{ ...record.advancedOptions }}
                        validation={validation}
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="end" alignItems="center" spacing={2}>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => setStep(1)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    // Ao editar o título
    useEffect(() => {
        if (updateCaret.current) handleTitleCaret();
    }, [state.record.title]);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    // Ao expandir dados opcionais
    useEffect(() => {
        setTimeout(() => editorRef.current?.focus(), 100);
    }, [expanded]);

    // Ao carregar registro para edição
    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
        else
            loadSettings();
    }, []);

    // Se carregado com menção
    useEffect(() => {
        setTimeout(() => {
            if (mentions) {
                for (var mention of mentions) {
                    editorRef.current?.insert("performaLink", mention);
                }
            }
            refTitle.current?.focus();
        }, 100);
    }, []);


    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "90%", maxWidth: "90%" } } : undefined}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t(state.record.publicationId == NIL ? "Publication.CreateQuestion.TitleNew" : "Publication.CreateQuestion.TitleEdit")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonExpandScreen
                            expanded={fullScreenMode}
                            size="small"
                            onClick={() => setFullScreenMode(!fullScreenMode)}
                        />
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
                {
                    responderId &&
                    <>
                        <Grid container justifyContent="space-between" sx={{ textTransform: "none" }}>
                            <Grid>
                                <Typography variant="subtitle1">
                                    {t("Publication.CreateQuestion.QuestionSubtitle", { name: props.responderName })}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box marginY={1} sx={{ textTransform: "none" }}>
                            <MprcCard paperColor="notice">
                                <Grid container alignItems="center">
                                    <Grid>
                                        <Typography
                                            variant="bodyNotice"
                                            marginLeft={1}
                                        >
                                            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                                        </Typography>
                                    </Grid>
                                    <Grid>
                                        <Typography
                                            component="p"
                                            variant="caption"
                                            padding={1}
                                        >
                                            <Trans
                                                i18nKey="Publication.CreateQuestion.ResponderMessage"
                                                t={t}
                                                components={AppTransComponents}
                                            />
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </MprcCard>
                        </Box>
                    </>
                }
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }
        </Dialog>
    );
};
